/**
 * WebAuthn 的 base64url ⇄ ArrayBuffer 转换。
 *
 * 为什么要手写这一层:
 * 服务端(go-webauthn)用 JSON 传 options, 里面的 challenge / user.id / credential id
 * 都是 **base64url 字符串**; 而浏览器的 navigator.credentials.create()/get() 要的是
 * **ArrayBuffer**。这层转换不做, 浏览器抛的是 "TypeError: Failed to execute 'create'"
 * 之类的话 —— 不会告诉你是哪个字段的类型不对。
 *
 * 为什么不用 PublicKeyCredential.parseCreationOptionsFromJSON():
 * 那套原生 API 好用, 但要 Chrome 119+ / Safari 17.4+。手写这十几行没有兼容性下限,
 * 而且出问题时能在自己的代码里断点 —— 认证流程调试起来本来就够难了。
 *
 * ⚠️ 必须是 base64**url**(-_ 而不是 +/, 无 = 填充)。用标准 base64 会让 challenge
 * 对不上, 报出来的是含混的 "challenge mismatch"。
 */

export function b64uToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

export function bufToB64u(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 浏览器是否具备 passkey 的前提条件。 */
export function passkeySupported(): boolean {
  // isSecureContext: WebAuthn 只在 HTTPS 和 localhost 下可用。http:// 页面里
  // navigator.credentials 存在但一调就抛 —— 先判这个才能给出说人话的提示。
  return typeof window !== 'undefined'
    && window.isSecureContext
    && typeof window.PublicKeyCredential !== 'undefined';
}

type AnyOpts = Record<string, unknown>;

/** 把服务端来的注册 options 里该是 ArrayBuffer 的字段就地转过来。 */
export function prepareCreationOptions(o: AnyOpts): PublicKeyCredentialCreationOptions {
  const opts = { ...o } as AnyOpts;
  opts.challenge = b64uToBuf(o.challenge as string);
  const user = { ...(o.user as AnyOpts) };
  user.id = b64uToBuf(user.id as string);
  opts.user = user;
  if (Array.isArray(o.excludeCredentials)) {
    opts.excludeCredentials = (o.excludeCredentials as AnyOpts[]).map((c) => ({
      ...c, id: b64uToBuf(c.id as string),
    }));
  }
  return opts as unknown as PublicKeyCredentialCreationOptions;
}

/** 把服务端来的登录 options 里该是 ArrayBuffer 的字段就地转过来。 */
export function prepareRequestOptions(o: AnyOpts): PublicKeyCredentialRequestOptions {
  const opts = { ...o } as AnyOpts;
  opts.challenge = b64uToBuf(o.challenge as string);
  if (Array.isArray(o.allowCredentials)) {
    opts.allowCredentials = (o.allowCredentials as AnyOpts[]).map((c) => ({
      ...c, id: b64uToBuf(c.id as string),
    }));
  }
  return opts as unknown as PublicKeyCredentialRequestOptions;
}

/** 把注册结果序列化成服务端(go-webauthn)认得的形状。 */
export function serializeAttestation(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    // transports 让下次登录时浏览器知道去哪找这把钥匙(USB / 内置 / 手机)。
    // 缺了不致命, 但会让跨设备的体验变差, 所以能给就给。
    transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      attestationObject: bufToB64u(r.attestationObject),
    },
  };
}

/** 把登录断言序列化成服务端认得的形状。 */
export function serializeAssertion(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      authenticatorData: bufToB64u(r.authenticatorData),
      signature: bufToB64u(r.signature),
      // userHandle 就是服务端的 person.ID。discoverable 登录靠它反查是谁,
      // 所以【不能省】—— 省了服务端只能退回用 credential id 查, 多一次失败可能。
      userHandle: r.userHandle ? bufToB64u(r.userHandle) : null,
    },
  };
}

/**
 * 把 WebAuthn 抛出的异常翻译成人能看懂的话。
 *
 * 原始报错基本没用: 用户按了取消、Touch ID 超时、RPID 配错、页面不是 HTTPS ——
 * 全都可能是同一个 NotAllowedError。不翻译的话, 人看到"操作不被允许"只会去点重试。
 */
export function explainWebAuthnError(e: unknown): string {
  const err = e as { name?: string; message?: string };
  switch (err?.name) {
    case 'NotAllowedError':
      return '已取消, 或等待超时。再点一次并完成 Touch ID 验证即可。';
    case 'InvalidStateError':
      return '这台设备上已经注册过了 —— 直接用「用 passkey 登录」即可, 不用重复注册。';
    case 'NotSupportedError':
      return '这个浏览器/设备不支持 passkey。';
    case 'SecurityError':
      // 这条最值得说清楚: 它几乎总是域名问题, 而人会去查网络和证书。
      return '域名不匹配: passkey 绑定在 console.pgv.sh.cn 上, 请确认地址栏就是这个域名(不是 IP、不是别的域名、必须 https)。';
    case 'AbortError':
      return '操作被中断。';
    default:
      return err?.message || '未知错误';
  }
}
